import type { ReactNode } from 'react';
import {
  Focusable,
  Tooltip as AriaTooltip,
  type TooltipProps as AriaTooltipProps,
  TooltipTrigger,
} from 'react-aria-components';
import { cn } from './lib/cn';

export type TooltipPlacement = NonNullable<AriaTooltipProps['placement']>;

export type TooltipProps = {
  /** The element the tooltip describes. Anything: text, icon, badge. */
  children: ReactNode;
  content: ReactNode;
  placement?: TooltipPlacement;
  /** Milliseconds before the first tooltip in a sequence opens. */
  delay?: number;
  /** Milliseconds it lingers after the pointer leaves. */
  closeDelay?: number;
  isDisabled?: boolean;
  /** Holds it open (true) or shut (false); left out, hover and focus decide. */
  isOpen?: boolean;
  /** Told whenever it opens or closes, by hover, focus or `isOpen`. */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * Set when `children` is already focusable and forwards props and ref — a
   * `Button`, say. Otherwise the trigger is wrapped in a focusable span.
   */
  asChild?: boolean;
  className?: string;
  /** Classes for the wrapper around `children`, e.g. to let it fill a menu. */
  triggerClassName?: string;
};

export function Tooltip({
  children,
  content,
  placement = 'top',
  delay = 100,
  closeDelay = 300,
  isDisabled,
  isOpen,
  onOpenChange,
  asChild,
  className,
  triggerClassName,
}: TooltipProps) {
  return (
    <TooltipTrigger
      delay={delay}
      closeDelay={closeDelay}
      isDisabled={isDisabled}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      {asChild ? (
        children
      ) : (
        <Focusable>
          <span
            // biome-ignore lint/a11y/noNoninteractiveTabindex: this span is the tooltip's trigger, and a trigger the keyboard cannot reach hides the tooltip from anyone not using a mouse
            tabIndex={0}
            className={cn(
              'sk:inline-flex sk:min-w-0 sk:max-w-full sk:outline-none',
              triggerClassName,
            )}
          >
            {children}
          </span>
        </Focusable>
      )}
      <AriaTooltip
        placement={placement}
        offset={6}
        className={cn(
          'sk:group sk:z-3000 sk:max-w-96 sk:rounded-card sk:px-3 sk:py-2',
          'sk:inset-shadow-hairline sk:bg-surface sk:shadow-card',
          /* `wrap-anywhere`: tooltips carry pasted URLs and tokens with no
             space to break at, which would drag the bubble past the screen. */
          'sk:wrap-anywhere sk:text-dense sk:leading-5 sk:text-fg sk:outline-none',
          className,
        )}
      >
        {content}
      </AriaTooltip>
    </TooltipTrigger>
  );
}
